import { ContentItemData } from './types';
import {
  HeroBannerContentFragmentFragment,
  HeroBannerWithFormContentFragmentFragment,
  HighlightContentFragmentFragment,
  TwoColumnPromoContentFragmentFragment,
} from '../../__generated__/graphql';
import { WithoutTypename } from '../../types';
import { ButtonColor } from '../Button/types';

const mapButtonColor = (color: string): ButtonColor => {
  switch (color) {
    case 'black':
      return 'black';
    case 'white':
      return 'white';
    case 'orange':
      return 'orange';
    case 'turquoise':
      return 'turquoise';
    default:
      throw new Error(`Unknown button color: ${color}`);
  }
};

const mapType = (type: string): ContentItemData['type'] => {
  switch (type) {
    case 'Button':
      return 'Button';
    case 'List':
      return 'List';
    case 'Paragraph':
      return 'Paragraph';
    case 'Wysiwyg':
      return 'Wysiwyg';
    default:
      throw new Error(`Unknown content type: ${type}`);
  }
};

const mapHeroBannerContentToContentItem = (
  content:
    | WithoutTypename<HeroBannerContentFragmentFragment>
    | WithoutTypename<HeroBannerWithFormContentFragmentFragment>,
): ContentItemData => {
  const type = mapType(content.type ?? '');

  const contentItem: ContentItemData = {
    type,
  };

  if (content.paragraph && type === 'Paragraph') {
    contentItem.paragraph = {
      bold: !!content.paragraph.bold,
      largeFontSize: false,
      removeTopMargin: !!content.paragraph.removeTopMargin,
      text: content.paragraph.text ?? '',
    };
  }

  if (content.button?.button && type === 'Button') {
    contentItem.button = {
      button: {
        target: content.button.button.target,
        title: content.button.button.title ?? '',
        url: content.button.button.url ?? '',
      },
      buttonColor: mapButtonColor(content.button.buttonColor ?? ''),
      buttonId: content.button.buttonId ?? '',
    };
  }

  if (content.list?.items && type === 'List') {
    contentItem.list = {
      dense: true,
      heading: content.list.heading ?? '',
      items: content.list.items.map(item => ({
        text: item?.text ?? '',
      })),
    };
  }

  if (type === 'Wysiwyg' && 'wysiwyg' in content) {
    const wysiwygContent = content;
    if (wysiwygContent.wysiwyg) {
      contentItem.wysiwyg = {
        wysiwyg: wysiwygContent.wysiwyg.wysiwyg ?? '',
      };
    }
  }

  return contentItem;
};

// This function is almost identical to mapHeroBannerContentToContent at the time of writing it.
// Making it separate to support future changes to types and provide a clear separation of concerns.
const mapTwoColumnPromoContentToContentItem = (
  content: TwoColumnPromoContentFragmentFragment,
): ContentItemData => {
  const type = mapType(content.type ?? '');

  const contentItem: ContentItemData = {
    type,
  };

  if (content.paragraph && type === 'Paragraph') {
    contentItem.paragraph = {
      bold: !!content.paragraph.bold,
      largeFontSize: !!content.paragraph.largeFontSize,
      removeTopMargin: !!content.paragraph.removeTopMargin,
      text: content.paragraph.text ?? '',
    };
  }

  if (content.button?.button && type === 'Button') {
    contentItem.button = {
      button: {
        target: content.button.button.target,
        title: content.button.button.title ?? '',
        url: content.button.button.url ?? '',
      },
      buttonColor: mapButtonColor(content.button.buttonColor ?? ''),
      buttonId: content.button.buttonId ?? '',
    };
  }

  if (content.list?.items && type === 'List') {
    contentItem.list = {
      dense: false,
      heading: content.list.heading ?? '',
      items: content.list.items.map(item => ({
        text: item?.text ?? '',
      })),
    };
  }

  return contentItem;
};

const mapHighlightContentToContentItem = (
  content: HighlightContentFragmentFragment,
): ContentItemData => {
  const type = mapType(content.type ?? '');
  const contentItem: ContentItemData = {
    type,
  };
  if (content.paragraph && type === 'Paragraph') {
    contentItem.paragraph = {
      bold: false,
      largeFontSize: false,
      removeTopMargin: false,
      text: content.paragraph.text ?? '',
    };
  }
  if (content.list?.items && type === 'List') {
    contentItem.list = {
      dense: false,
      heading: content.list.heading ?? '',
      items: content.list.items.map(item => ({
        text: item?.text ?? '',
      })),
    };
  }

  return contentItem;
};

export const mapHeroBannerContent = (
  contentItems: (
    | null
    | WithoutTypename<HeroBannerContentFragmentFragment>
    | WithoutTypename<HeroBannerWithFormContentFragmentFragment>
  )[],
): ContentItemData[] => (contentItems.filter(Boolean) as (
  | WithoutTypename<HeroBannerContentFragmentFragment>
  | WithoutTypename<HeroBannerWithFormContentFragmentFragment>
)[])
  .map(mapHeroBannerContentToContentItem);

export const mapTwoColumnPromoContent = (
  contentItems: (null | TwoColumnPromoContentFragmentFragment)[],
): ContentItemData[] => (contentItems.filter(Boolean) as TwoColumnPromoContentFragmentFragment[])
  .map(mapTwoColumnPromoContentToContentItem);

export const mapHighlightContent = (
  contentItems: (HighlightContentFragmentFragment | null)[],
): ContentItemData[] => (contentItems.filter(Boolean) as HighlightContentFragmentFragment[])
  .map(mapHighlightContentToContentItem);
